/**
 * WoWyrd — Typed API client for the Rust fraud prevention service.
 * Base URL: VITE_API_URL (default: http://localhost:8090)
 */

import type {
  EvaluationResult,
  BlockResponse,
  BlockedIdentifiersResponse,
  HealthStatus,
  ReportSummary,
  RecentFeedResponse,
  LicenseStatus,
  BlockType,
  CaseDetailResponse,
  CaseListResponse,
  CaseStatus,
  Rule,
  CalibrationProposal,
  AuditLogResponse,
  Operator,
  ActionGateState,
  ActionGateProposal,
  GatedActionType,
  ActionMappingEntry,
  StepUpThresholdCurrent,
  WebhookDelivery,
  WebhookEndpoint,
  WebhookEndpointCreated,
  WebhookEvent,
  WebhookSecretRotated,
  ManagerKpis,
  AuthorReview,
  SoundnessReview,
  SoundnessOutcome,
} from '../types/risk';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8090';
const API_KEY = import.meta.env.VITE_API_KEY ?? '';

// specs/control-api/rule-weight-calibration.md — calibration/action-gate/webhook routes are
// gated by require_operator, a stricter, individually-attributed tier from the shared
// VITE_API_KEY every other route uses. Not client-facing (internal WoWyrd-operator tool).
//
// specs/control-api/dashboard-key-scoping.md (Gate 1, 2026-11-01) replaced the original
// "paste the raw operator key, hold it in localStorage forever" shape with a short-lived
// session-token exchange: the raw key is sent once to POST /v1/auth/session and never stored
// anywhere after that; only the resulting opaque, short-lived session token is held, and only
// in memory (a module-level variable here) — never localStorage, so it doesn't survive a page
// reload (an 8-hour-old token sitting in storage indefinitely would defeat the point of a
// short-lived credential). A reload simply re-prompts, same UX cost as any login session
// expiring, not a new inconvenience this spec introduced.
let operatorSessionToken: string | null = null;

/** Whether an operator session is currently held in memory — the only thing the three gated
 * tabs (Calibration/ActionGates/Webhooks) need to know, not the token's own value. */
export function hasOperatorSession(): boolean {
  return operatorSessionToken !== null;
}

export function clearOperatorSession(): void {
  operatorSessionToken = null;
}

interface CreateSessionResponse {
  session_token: string;
  expires_at: string;
}

/** Exchanges a raw operator key for a short-lived session token via `POST /v1/auth/session`.
 * Throws on an invalid/revoked key — the caller (a UI form handler) is expected to catch this
 * and show an error, not treat a thrown exception as unexpected. */
export async function createOperatorSession(rawKey: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/v1/auth/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operator_key: rawKey }),
  });
  if (!res.ok) {
    throw new Error(`session exchange failed (${res.status})`);
  }
  const body = (await res.json()) as CreateSessionResponse;
  operatorSessionToken = body.session_token;
}

async function requestWithKey<T>(path: string, key: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(key ? { Authorization: `Bearer ${key}` } : {}),
    },
    ...init,
  });
  if (!res.ok) {
    // Never surface a raw response body verbatim -- a proxy/edge rejection (e.g. an nginx 403
    // page in front of a locked-down public instance) returns full HTML here, not a structured
    // API error, and dumping it into the UI reads as a broken page rather than a clear message.
    const contentType = res.headers.get('content-type') ?? '';
    let detail = `request failed (${res.status})`;
    if (contentType.includes('application/json')) {
      try {
        const errBody = await res.json();
        detail = errBody?.error ?? errBody?.message ?? JSON.stringify(errBody);
      } catch {
        // Keep the generic detail above -- the body claimed to be JSON but wasn't parseable.
      }
    }
    throw new Error(`risk API ${path}: ${res.status} ${detail}`);
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

function request<T>(path: string, init?: RequestInit): Promise<T> {
  return requestWithKey<T>(path, API_KEY, init);
}

/** Every request in this section uses the operator's own session token, not the shared
 * VITE_API_KEY and, since specs/control-api/dashboard-key-scoping.md, no longer the raw
 * operator key either. */
function operatorRequest<T>(path: string, init?: RequestInit): Promise<T> {
  return requestWithKey<T>(path, operatorSessionToken ?? '', init);
}

// ---- Evaluate ----

export interface EvaluateParams {
  ip: string;
  customer_id?: string;
  device_fp?: string;
  card_bin?: number;
  session_id?: string;
  email?: string;
}

export function evaluate(params: EvaluateParams): Promise<EvaluationResult> {
  return request<EvaluationResult>('/v1/evaluate', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// ---- Feedback ----

export interface FeedbackParams {
  type: 'BANK_DECLINE' | 'CHARGEBACK';
  transaction_id: string;
  customer_id?: string;
  device_fp?: string;
  card_bin?: number;
  ip?: string;
  amount?: number;
  currency?: string;
  notes?: string;
}

export interface FeedbackResponse {
  feedback_id: string;
  type: string;
  block_triggered: boolean;
  blocked_ids?: string[];
  decline_count?: number;
}

export function submitFeedback(params: FeedbackParams): Promise<FeedbackResponse> {
  return request<FeedbackResponse>('/v1/feedback', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// ---- Blocking ----

export interface BlockParams {
  type: BlockType;
  value: string;
  reason?: string;
  ttl_hours?: number;
  notes?: string;
}

// specs/control-api/dashboard-key-scoping.md (Gate 1, 2026-11-01) — createBlock/deleteBlock
// moved off the shared VITE_API_KEY onto a real operator session: `created_by` is now the
// server-resolved operator identity, never a caller-supplied value, closing the same gap
// `X-Operator-ID` used to leave open.
export function createBlock(params: BlockParams): Promise<BlockResponse> {
  return operatorRequest<BlockResponse>('/v1/block', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function deleteBlock(type: BlockType, value: string): Promise<void> {
  return operatorRequest<void>(`/v1/block/${type}/${encodeURIComponent(value)}`, {
    method: 'DELETE',
  });
}

export interface ListBlocksParams {
  type?: BlockType;
  limit?: number;
  offset?: number;
}

export function listBlocks(params: ListBlocksParams = {}): Promise<BlockedIdentifiersResponse> {
  const qs = new URLSearchParams();
  if (params.type) qs.set('type', params.type);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return request<BlockedIdentifiersResponse>(`/v1/block${query}`);
}

// ---- Case-review queue ----

export interface ListCasesParams {
  status?: 'open' | 'resolved';
  decision?: string; // comma-separated, e.g. "REVIEW,BLOCK"
  limit?: number;
  offset?: number;
}

export function listCases(params: ListCasesParams = {}): Promise<CaseListResponse> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.decision) qs.set('decision', params.decision);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return request<CaseListResponse>(`/v1/cases${query}`);
}

export interface SubmitCaseReviewParams {
  status: CaseStatus;
  notes: string;
}

// specs/control-api/dashboard-key-scoping.md — moved off the shared VITE_API_KEY onto a real
// operator session, same reasoning as createBlock/deleteBlock above.
export function submitCaseReview(
  assessmentId: string,
  params: SubmitCaseReviewParams
): Promise<{ assessment_id: string; status: string; reviewed_at: string }> {
  return operatorRequest(`/v1/cases/${encodeURIComponent(assessmentId)}/review`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// ---- Case detail + export (specs/reporting/case-detail-view-and-regulator-export.md) ----

export function getCaseDetail(assessmentId: string): Promise<CaseDetailResponse> {
  return request<CaseDetailResponse>(`/v1/cases/${encodeURIComponent(assessmentId)}`);
}

/** Raw `fetch`, not `request()` -- the HTML export is `text/html`, not JSON, so it can't go
 * through `requestWithKey`'s `res.json()` call. Reused for `format=json` too, for a single
 * consistent "fetch then trigger a browser download" path for both export formats. */
export async function fetchCaseExport(assessmentId: string, format: 'html' | 'json'): Promise<Blob> {
  const res = await fetch(`${BASE_URL}/v1/cases/${encodeURIComponent(assessmentId)}/export?format=${format}`, {
    headers: API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {},
  });
  if (!res.ok) {
    throw new Error(`case export (${format}) failed: ${res.status}`);
  }
  return res.blob();
}

// ---- Manager KPI view (specs/reporting/manager-kpi-view.md) ----
// Same client API-key tier as evaluate/cases above — a client's own risk-ops team, not
// require_operator (that tier is WoWyrd-engineer-only, used by calibration/action-gates below).

export interface ListManagerKpisParams {
  from?: string; // RFC3339
  to?: string; // RFC3339
}

export function getManagerKpis(params: ListManagerKpisParams = {}): Promise<ManagerKpis> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return request<ManagerKpis>(`/v1/reports/manager-kpis${query}`);
}

// ---- Author view (specs/reporting/author-view.md) ----
// Same client API-key tier as Manager KPI/Cases above — a soundness review documents
// oversight, it never changes a live rule parameter, so it stays out of the
// require_operator calibration/action-gates tier below despite touching fraud_rules too.

export interface ListAuthorReviewParams {
  from?: string; // RFC3339
  to?: string; // RFC3339
}

export function getAuthorReview(params: ListAuthorReviewParams = {}): Promise<AuthorReview> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return request<AuthorReview>(`/v1/reports/author-review${query}`);
}

export interface SubmitSoundnessReviewParams {
  outcome: SoundnessOutcome;
  notes: string;
}

// specs/control-api/dashboard-key-scoping.md — moved off the shared VITE_API_KEY onto a real
// operator session, same reasoning as createBlock/deleteBlock above. listSoundnessReviews
// below is unaffected — it's a read, moved to the dashboard's own read-only key tier
// server-side, not this one.
export function submitSoundnessReview(
  ruleId: string,
  params: SubmitSoundnessReviewParams
): Promise<SoundnessReview> {
  return operatorRequest(`/v1/rules/${encodeURIComponent(ruleId)}/soundness-reviews`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function listSoundnessReviews(ruleId: string): Promise<SoundnessReview[]> {
  return request(`/v1/rules/${encodeURIComponent(ruleId)}/soundness-reviews`);
}

// ---- Rule-weight calibration (specs/control-api/rule-weight-calibration.md) ----
// Operator-gated — every function here sends the operator's own key (getOperatorKey()),
// never the shared VITE_API_KEY the rest of this file uses.

export function whoami(): Promise<Operator> {
  return operatorRequest<Operator>('/v1/me');
}

export function listRules(): Promise<Rule[]> {
  return operatorRequest<Rule[]>('/v1/rules');
}

export interface ProposeCalibrationParams {
  proposed_weight: number;
  notes?: string;
}

export function proposeCalibration(
  ruleId: string,
  params: ProposeCalibrationParams
): Promise<{ id: string }> {
  return operatorRequest(`/v1/rules/${encodeURIComponent(ruleId)}/calibration-proposals`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function listPendingProposals(): Promise<CalibrationProposal[]> {
  return operatorRequest<CalibrationProposal[]>('/v1/calibration-proposals');
}

/**
 * specs/control-api/rule-weight-calibration.md's "Round 3" (2026-11-01) — the full,
 * WoWyrd-internal-operator-only audit log of resolved (approved/rejected) calibration
 * changes, including each row's effective_at (or its honest absence). Deliberately
 * unwindowed — see handle_list_audit_log's own doc comment in rules.rs for why.
 */
export function listCalibrationAuditLog(): Promise<AuditLogResponse> {
  return operatorRequest<AuditLogResponse>('/v1/calibration-proposals/audit-log');
}

export function approveCalibration(proposalId: string): Promise<{ approved: boolean }> {
  return operatorRequest(`/v1/calibration-proposals/${encodeURIComponent(proposalId)}/approve`, {
    method: 'POST',
  });
}

export function rejectCalibration(
  proposalId: string,
  reason?: string
): Promise<{ rejected: boolean }> {
  return operatorRequest(`/v1/calibration-proposals/${encodeURIComponent(proposalId)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ---- Action-approval gate (specs/control-api/action-approval-gate.md) ----
// Same require_operator tier as calibration above — every function here sends the operator's
// own key (getOperatorKey()), never the shared VITE_API_KEY.

// ---- Step-up client API (specs/control-api/step-up-client-api.md) ----
// Read-only, require_operator tier — same as action-gates above. Propose/approve/reject stays
// API/curl-only for this first slice; the dashboard only shows the current resolved state.

export function getActionMapping(): Promise<ActionMappingEntry[]> {
  return operatorRequest<ActionMappingEntry[]>('/v1/action-mapping');
}

export function getStepUpThreshold(): Promise<StepUpThresholdCurrent> {
  return operatorRequest<StepUpThresholdCurrent>('/v1/step-up-threshold');
}

export function listActionGates(): Promise<ActionGateState[]> {
  return operatorRequest<ActionGateState[]>('/v1/action-gates');
}

export interface ProposeActionGateChangeParams {
  requested_enabled: boolean;
  notes?: string;
}

export function proposeActionGateChange(
  actionType: GatedActionType,
  params: ProposeActionGateChangeParams
): Promise<{ id: string }> {
  return operatorRequest(`/v1/action-gates/${encodeURIComponent(actionType)}/proposals`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function listPendingActionGateProposals(): Promise<ActionGateProposal[]> {
  return operatorRequest<ActionGateProposal[]>('/v1/action-gates/proposals');
}

export function approveActionGateChange(proposalId: string): Promise<{ approved: boolean }> {
  return operatorRequest(`/v1/action-gates/proposals/${encodeURIComponent(proposalId)}/approve`, {
    method: 'POST',
  });
}

export function rejectActionGateChange(
  proposalId: string,
  reason?: string
): Promise<{ rejected: boolean }> {
  return operatorRequest(`/v1/action-gates/proposals/${encodeURIComponent(proposalId)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ---- Webhook endpoints (specs/control-api/webhook-notifications.md,
// specs/control-api/webhooks-dashboard.md) ----
// Same require_operator tier as calibration/action-gates above.

export function listWebhookEndpoints(): Promise<WebhookEndpoint[]> {
  return operatorRequest<WebhookEndpoint[]>('/v1/webhooks/endpoints');
}

export interface CreateWebhookEndpointParams {
  url: string;
  subscribed_events: WebhookEvent[];
}

export function createWebhookEndpoint(
  params: CreateWebhookEndpointParams
): Promise<WebhookEndpointCreated> {
  return operatorRequest('/v1/webhooks/endpoints', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function deleteWebhookEndpoint(id: string): Promise<{ deleted: boolean }> {
  return operatorRequest(`/v1/webhooks/endpoints/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function reenableWebhookEndpoint(id: string): Promise<{ reenabled: boolean }> {
  return operatorRequest(`/v1/webhooks/endpoints/${encodeURIComponent(id)}/reenable`, {
    method: 'POST',
  });
}

/** specs/control-api/webhook-delivery-history.md — newest-first, paginated. */
export function listWebhookDeliveries(id: string, limit = 20): Promise<WebhookDelivery[]> {
  return operatorRequest<WebhookDelivery[]>(
    `/v1/webhooks/endpoints/${encodeURIComponent(id)}/deliveries?limit=${limit}`
  );
}

/** specs/control-api/webhook-secret-rotation.md — returns the fresh secret exactly once. */
export function rotateWebhookSecret(id: string): Promise<WebhookSecretRotated> {
  return operatorRequest(`/v1/webhooks/endpoints/${encodeURIComponent(id)}/rotate-secret`, {
    method: 'POST',
  });
}

// ---- Health ----

export function getHealth(signal?: AbortSignal): Promise<HealthStatus> {
  return request<HealthStatus>('/health', signal ? { signal } : undefined);
}

// ---- Evidence report summary (feed-counter preload) ----

/** specs/simulation-lab/demo-neobank-traffic.md -- `from`/`to` default server-side to the last
 * 7 days if omitted; the caller passes a tighter window (e.g. 24h) for a demo preload so an
 * old, now-irrelevant total doesn't linger after a VPS-side reset. Reads only `summary` off the
 * much larger GET /v1/reports/evidence response -- see ReportSummary's own doc comment. */
export function getEvidenceReportSummary(
  fromIso: string,
  signal?: AbortSignal
): Promise<{ summary: ReportSummary }> {
  const params = new URLSearchParams({ from: fromIso, format: 'json' });
  return request<{ summary: ReportSummary }>(
    `/v1/reports/evidence?${params.toString()}`,
    signal ? { signal } : undefined
  );
}

// ---- Recent feed (row-level feed preload) ----

/** specs/simulation-lab/demo-neobank-traffic.md -- the row-level half of the feed/counters
 * "first impression" fix: the last `limit` real transactions (newest first, every decision
 * included), fetched once on `TransactionFeed` mount and used to seed `riskStore.seedEvents()`
 * before the first WebSocket event arrives. Sibling of `getEvidenceReportSummary` above (same
 * "counts only" preload), but for the actual rows, not just the aggregate totals. */
export function fetchRecentFeed(limit = 200, signal?: AbortSignal): Promise<RecentFeedResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  return request<RecentFeedResponse>(
    `/v1/feed/recent?${params.toString()}`,
    signal ? { signal } : undefined
  );
}

// ---- License status ----

export function getLicenseStatus(): Promise<LicenseStatus> {
  return request<LicenseStatus>('/v1/license-status');
}

// ---- WebSocket URL ----

export function getWebSocketURL(): string {
  const wsBase = BASE_URL.replace(/^http/, 'ws');
  // WebSocket upgrades can't carry a custom Authorization header from a browser —
  // the token travels as a query param instead; see interfaces::http::auth on the server.
  const token = API_KEY ? `?token=${encodeURIComponent(API_KEY)}` : '';
  return `${wsBase}/v1/ws/events${token}`;
}
